<script setup>
import { ref, computed, reactive } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import PasswordRequirements from './PasswordRequirements.vue';
import { isValidPassword } from 'shared/helpers/Validators';
import GoogleOAuthButton from 'v3/components/GoogleOauth/Button.vue';
import { register } from 'v3/api/auth';
import * as CompanyEmailValidator from 'company-email-validator';

const MIN_PASSWORD_LENGTH = 6;

const store = useStore();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const selectedPlan = computed(() => route.query.plan || '');

const hCaptcha = ref(null);
const isPasswordFocused = ref(false);
const isSignupInProgress = ref(false);

const extraData = reactive({
  fullName: '',
  companySize: '',
});

const credentials = reactive({
  email: '',
  password: '',
  hCaptchaClientResponse: '',
});

const isPasswordValid = computed(() => {
  const password = credentials.password || '';
  const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=[\]{}|'"/\\.,`<>:;?~]/;
  return (
    password.length >= MIN_PASSWORD_LENGTH &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    SPECIAL_CHAR_REGEX.test(password)
  );
});

const rules = {
  credentials: {
    email: {
      required,
      email,
    },
    password: {
      required,
      isValidPassword,
      minLength: minLength(MIN_PASSWORD_LENGTH),
    },
  },
};

const v$ = useVuelidate(rules, { credentials });

const globalConfig = computed(() => store.getters['globalConfig/get']);

const termsLink = computed(() =>
  t('REGISTER.TERMS_ACCEPT')
    .replace('https://www.onlinesavaari.com/terms', globalConfig.value.termsURL)
    .replace(
      'https://www.onlinesavaari.com/privacy-policy',
      globalConfig.value.privacyURL
    )
);

const allowedLoginMethods = computed(
  () => window.osdeskConfig.allowedLoginMethods || ['email']
);

const showGoogleOAuth = computed(
  () =>
    allowedLoginMethods.value.includes('google_oauth') &&
    Boolean(window.osdeskConfig.googleOAuthClientId)
);

const isFormValid = computed(() => !v$.value.$invalid);

const performRegistration = async () => {
  isSignupInProgress.value = true;
  try {
    await register({
      email: credentials.email,
      password: credentials.password,
      hCaptchaClientResponse: credentials.hCaptchaClientResponse,
      fullName: extraData.fullName,
      subscriptionPlan: selectedPlan.value,
    });
    router.push({
      name: 'auth_verify_email',
      state: { email: credentials.email },
    });
  } catch (error) {
    const errorMessage = error?.message || t('REGISTER.API.ERROR_MESSAGE');
    if (globalConfig.value.hCaptchaSiteKey) {
      hCaptcha.value.reset();
      credentials.hCaptchaClientResponse = '';
    }
    useAlert(errorMessage);
  } finally {
    isSignupInProgress.value = false;
  }
};

const submit = () => {
  if (isSignupInProgress.value) return;
  v$.value.$touch();
  if (v$.value.$invalid) {
    if (v$.value.credentials.email.$invalid) {
      useAlert('Please enter a valid work email.');
    } else if (v$.value.credentials.password.$invalid) {
      useAlert('Please enter a password meeting all requirements.');
    } else {
      useAlert('Please fill out all fields correctly.');
    }
    return;
  }
  isSignupInProgress.value = true;
  if (globalConfig.value.hCaptchaSiteKey) {
    hCaptcha.value.execute();
  } else {
    performRegistration();
  }
};

const onRecaptchaVerified = token => {
  credentials.hCaptchaClientResponse = token;
  performRegistration();
};

const onCaptchaError = () => {
  isSignupInProgress.value = false;
  credentials.hCaptchaClientResponse = '';
  hCaptcha.value.reset();
};
</script>

<template>
  <div
    class="scroll-smooth h-screen w-full flex flex-col md:flex-row bg-surface overflow-hidden overflow-x-hidden"
  >
    <!-- Left Section: Branding & Social Proof -->
    <section
      class="relative w-full md:w-1/2 bg-deep-navy flex flex-col justify-between p-6 md:p-12 overflow-hidden h-full"
    >
      <!-- Background Animation Layer -->
      <div class="absolute inset-0 opacity-40" />
      <!-- Header -->
      <div class="relative z-10 flex items-center gap-stack-sm">
        <span class="material-symbols-outlined text-secondary-fixed text-[32px]">deployed_code</span>
        <h1
          class="font-headline-md text-headline-md text-surface-bright font-bold tracking-tight"
        >
          OS DESK
        </h1>
      </div>
      <!-- Content Area -->
      <div class="relative z-10 mt-stack-xl md:mt-0">
        <h2
          class="font-headline-lg text-headline-lg text-surface-bright max-w-lg mb-stack-md leading-tight"
        >
          Scale your support with
          <span class="text-secondary-fixed">Architectural Precision</span>.
        </h2>
        <p
          class="font-body-lg text-body-lg text-surface-variant max-w-md mb-stack-lg"
        >
          Experience the next generation of helpdesk management. Our AI-native
          engine resolves tickets before your agents even see them.
        </p>
        <!-- Value Chips -->
        <div class="flex flex-wrap gap-stack-md mb-stack-xl">
          <div
            class="flex items-center gap-2 px-4 py-2 bg-primary-container rounded-full border border-white/10"
          >
            <span class="material-symbols-outlined text-secondary-fixed text-sm">check_circle</span>
            <span class="text-caption font-label-md text-surface-bright">14-day free trial</span>
          </div>
          <div
            class="flex items-center gap-2 px-4 py-2 bg-primary-container rounded-full border border-white/10"
          >
            <span class="material-symbols-outlined text-secondary-fixed text-sm">credit_card_off</span>
            <span class="text-caption font-label-md text-surface-bright">No credit card required</span>
          </div>
        </div>
        <!-- AI Intelligence Widget (Visual) -->
        <div
          class="glass-card p-6 rounded-xl animate-float max-w-sm hidden md:block"
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-8 h-8 rounded-full bg-secondary-container flex items-center justify-center"
            >
              <span
                class="material-symbols-outlined text-on-secondary-container text-lg"
                style="font-variation-settings: 'FILL' 1"
                >auto_awesome</span>
            </div>
            <div>
              <p class="text-label-md font-label-md text-primary">
                Resolution Intelligence
              </p>
              <p class="text-caption text-on-surface-variant">
                Real-time optimization active
              </p>
            </div>
          </div>
          <div class="space-y-3">
            <div
              class="h-2 w-full bg-surface-variant rounded-full overflow-hidden"
            >
              <div class="h-full bg-secondary w-3/4" />
            </div>
            <p class="text-caption text-secondary font-semibold">
              94% Confidence Score
            </p>
          </div>
        </div>
      </div>
      <!-- Footer: Social Proof -->
      <div class="relative z-10 pt-stack-xl">
        <p
          class="text-caption font-label-md text-surface-variant uppercase tracking-widest mb-stack-md"
        >
          Trusted by Global Leaders
        </p>
        <div class="flex flex-col gap-stack-sm">
          <div class="flex -space-x-3 overflow-hidden mb-2">
            <img
              class="inline-block h-10 w-10 rounded-full ring-2 ring-deep-navy"
              data-alt="Close-up studio portrait of a professional female executive in a modern tech office, soft natural lighting, vibrant blue and white corporate palette."
              src="/brand-assets/portfolio/stitch_792fb2db6412.jpg"
            />
            <img
              class="inline-block h-10 w-10 rounded-full ring-2 ring-deep-navy"
              data-alt="Close-up studio portrait of a smiling male operations manager with spectacles, high-end commercial photography, clean minimalist background with deep navy accents."
              src="/brand-assets/portfolio/stitch_38074da8dd7e.jpg"
            />
            <img
              class="inline-block h-10 w-10 rounded-full ring-2 ring-deep-navy"
              data-alt="Close-up portrait of a diverse tech leader, warm professional lighting, modern minimalist glass office setting, shallow depth of field."
              src="/brand-assets/portfolio/stitch_07fac487704d.jpg"
            />
            <div
              class="h-10 w-10 rounded-full bg-secondary-fixed flex items-center justify-center ring-2 ring-deep-navy"
            >
              <span class="text-on-secondary-fixed text-xs font-bold">+30k</span>
            </div>
          </div>
          <p class="text-body-md font-body-md text-surface-bright">
            Join 30,000+ teams resolving better with AI.
          </p>
        </div>
      </div>
    </section>
    <!-- Right Section: Signup Form -->
    <section
      class="w-full md:w-1/2 bg-surface flex flex-col items-center p-6 md:p-12 h-full overflow-y-auto overflow-x-hidden"
    >
      <div class="w-full max-w-md my-auto py-8">
        <div class="mb-stack-md text-left">
          <router-link
            to="/"
            class="inline-flex items-center gap-2 text-caption text-on-surface-variant hover:text-primary transition-colors font-medium"
          >
            <span class="material-symbols-outlined text-[16px]">arrow_back</span>
            Back to Home
          </router-link>
        </div>
        <div class="mb-stack-lg">
          <h3
            class="font-headline-md text-headline-md text-on-surface mb-stack-xs"
          >
            Create your workspace
          </h3>
          <p class="text-body-md font-body-md text-on-surface-variant">
            Start your 14-day trial of OS DESK AI today.
          </p>
        </div>
        <form class="space-y-stack-md" @submit.prevent="submit">
          <!-- Full Name -->
          <div class="space-y-1">
            <label
              class="font-label-md text-label-md text-on-surface-variant"
              for="name"
              >Full Name</label>
            <div class="relative">
              <span
                class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline-variant"
                >person</span>
              <input
                id="name"
                v-model="extraData.fullName"
                class="w-full pl-10 pr-4 py-3 bg-white border border-outline-variant rounded-lg focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition-all font-body-md"
                placeholder="Alex Rivera"
                type="text"
                required
              />
            </div>
          </div>
          <!-- Work Email -->
          <div class="space-y-1">
            <label
              class="font-label-md text-label-md text-on-surface-variant"
              for="email"
              >Work Email</label>
            <div class="relative">
              <span
                class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline-variant"
                >mail</span>
              <input
                id="email"
                v-model="credentials.email"
                class="w-full pl-10 pr-4 py-3 bg-white border border-outline-variant rounded-lg focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition-all font-body-md"
                placeholder="alex@company.com"
                type="email"
                required
              />
            </div>
          </div>
          <!-- Company Size -->
          <div class="space-y-1">
            <label
              class="font-label-md text-label-md text-on-surface-variant"
              for="size"
              >Company Size</label>
            <div class="relative">
              <span
                class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline-variant"
                >groups</span>
              <select
                id="size"
                v-model="extraData.companySize"
                class="w-full pl-10 pr-4 py-3 bg-white border border-outline-variant rounded-lg focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition-all font-body-md appearance-none"
                required
              >
                <option disabled="" value="">Select size</option>
                <option value="1-10">1 - 10 employees</option>
                <option value="11-50">11 - 50 employees</option>
                <option value="51-200">51 - 200 employees</option>
                <option value="201-500">201 - 500 employees</option>
                <option value="500+">500+ employees</option>
              </select>
              <span
                class="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-outline-variant pointer-events-none"
                >expand_more</span>
            </div>
          </div>
          <!-- Password -->
          <div class="space-y-1">
            <label
              class="font-label-md text-label-md text-on-surface-variant"
              for="password"
              >Password</label>
            <div class="relative group">
              <span
                class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline-variant"
                >lock</span>
              <input
                id="password"
                v-model="credentials.password"
                class="w-full pl-10 pr-10 py-3 bg-white border border-outline-variant rounded-lg focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition-all font-body-md"
                placeholder="••••••••"
                required
                type="password"
                @focus="isPasswordFocused = true"
                @blur="isPasswordFocused = false"
              />
              <PasswordRequirements
                v-if="isPasswordFocused && !isPasswordValid"
                :password="credentials.password"
              />
            </div>
          </div>
          <!-- CTA -->
          <button
            class="w-full py-4 ai-gradient-button text-white font-label-md text-label-md rounded-lg shadow-lg hover:shadow-secondary/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 group"
            type="submit"
          >
            Get Started with AI
            <span
              class="material-symbols-outlined text-[20px] group-hover:translate-x-1 transition-transform"
              >arrow_forward</span>
          </button>
        </form>
        <!-- VueHcaptcha Component Restored -->
        <VueHcaptcha
          v-if="globalConfig.hCaptchaSiteKey"
          ref="hCaptcha"
          size="invisible"
          :sitekey="globalConfig.hCaptchaSiteKey"
          @verify="onRecaptchaVerified"
          @error="onCaptchaError"
          @expired="onCaptchaError"
          @challenge-expired="onCaptchaError"
          @closed="onCaptchaError"
        />
        <!-- Social Signup -->
        <div class="mt-stack-lg">
          <div class="relative flex items-center py-stack-md">
            <div class="flex-grow border-t border-outline-variant" />
            <span
              class="flex-shrink mx-4 text-caption font-label-md text-outline"
              >OR SIGN UP WITH</span>
            <div class="flex-grow border-t border-outline-variant" />
          </div>
          <div class="grid grid-cols-2 gap-gutter">
            <button
              class="flex items-center justify-center gap-2 py-3 bg-white border border-outline-variant rounded-lg hover:bg-surface-container-low transition-colors font-label-md text-label-md"
            >
              <svg class="w-5 h-5" viewbox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 12-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google
            </button>
            <button
              class="flex items-center justify-center gap-2 py-3 bg-white border border-outline-variant rounded-lg hover:bg-surface-container-low transition-colors font-label-md text-label-md"
            >
              <svg class="w-5 h-5 fill-[#000000]" viewbox="0 0 24 24">
                <path
                  d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.51 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2.04-.156-3.03 1.04-4.01 1.04zm1.91-4.831c.857-1.039 1.442-2.481 1.287-3.921-1.234.052-2.727.819-3.61 1.857-.793.922-1.48 2.389-1.287 3.805 1.378.104 2.753-.69 3.61-1.741z"
                />
              </svg>
              Apple
            </button>
          </div>
        </div>
        <!-- Legal/Login Link -->
        <div class="mt-stack-xl text-center space-y-4">
          <p class="text-caption text-on-surface-variant">
            By signing up, you agree to our
            <router-link
              class="text-primary font-semibold hover:underline"
              to="/terms"
            >
              Terms
            </router-link>
            and
            <router-link
              class="text-primary font-semibold hover:underline"
              to="/privacy"
            >
              Privacy Policy
</router-link>.
          </p>
          <p class="text-body-md font-body-md text-on-surface-variant">
            Already have an account?
            <router-link
              class="text-secondary font-bold hover:text-secondary-fixed-dim transition-colors"
              to="/app/login"
            >
              Log in
            </router-link>
          </p>
        </div>
      </div>
    </section>

    <!-- Micro-interaction Script -->
  </div>
</template>
